/**
 * إعدادات الموقع: التصنيفات (معرّفات slug) والعملة (تُجلب من API)
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';
import { DEFAULT_CATEGORY_SLUGS } from '../constants/categories';
import { CategoryNode } from '../types';
import { normalizeHeroHomeTextConfig, type HeroHomeTextConfig } from '../types/heroHomeText';

/** Arabic fallbacks when i18n key missing (should not occur for known symbols). */
export const CURRENCY_LABELS: Record<string, string> = {
  'د.أ': 'دينار أردني',
  'ر.س': 'ريال سعودي',
  'د.ك': 'دينار كويتي',
  'د.إ': 'درهم إماراتي',
  'ي.د': 'دينار أردني',
  'ج.م': 'جنيه مصري',
  $: 'دولار',
  '€': 'يورو'
};

const CURRENCY_I18N_KEY: Record<string, string> = {
  'د.أ': 'dinarJo',
  'ي.د': 'dinarJo',
  'ر.س': 'sar',
  'د.ك': 'kwd',
  'د.إ': 'aed',
  'ج.م': 'egp',
  $: 'usd',
  '€': 'eur'
};

interface SiteSettingsContextType {
  categories: CategoryNode[];
  currencySymbol: string;
  currencyLabel: string;
  heroVideoUrl: string | null;
  /** Custom hero overlay copy from DB; null → use i18n `home.*` defaults. */
  heroHomeText: HeroHomeTextConfig | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const defaultCurrency = 'د.أ';

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  categories: DEFAULT_CATEGORY_SLUGS.map(s => ({ id: s, name: s, children: [], attributes: [] })),
  currencySymbol: defaultCurrency,
  currencyLabel: CURRENCY_LABELS[defaultCurrency] || defaultCurrency,
  heroVideoUrl: null,
  heroHomeText: null,
  loading: true,
  refresh: async () => {}
});

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  return ctx;
}

interface SiteSettingsProviderProps {
  children: ReactNode;
}

export function SiteSettingsProvider({ children }: SiteSettingsProviderProps) {
  const { t, i18n } = useTranslation('common');
  const [categories, setCategories] = useState<CategoryNode[]>(
    DEFAULT_CATEGORY_SLUGS.map(s => ({ id: s, name: s, children: [], attributes: [] }))
  );
  const [currencySymbol, setCurrencySymbol] = useState<string>(defaultCurrency);
  const [heroVideoUrl, setHeroVideoUrl] = useState<string | null>(null);
  const [heroHomeText, setHeroHomeText] = useState<HeroHomeTextConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async (showErrorToast = false) => {
    try {
      const res = await axios.get<{
        categories: any[];
        currencySymbol: string;
        heroVideoUrl?: string | null;
        heroHomeText?: unknown;
        fromDatabase?: boolean;
      }>(`${API_BASE_URL}/settings`, {
        timeout: 8000
      });
      if (Array.isArray(res.data.categories)) {
        const rawCats = res.data.categories;
        const normalized: CategoryNode[] = rawCats.map(cat => {
          if (typeof cat === 'string') {
            return { id: cat, name: cat, children: [], attributes: [] };
          }
          return cat as CategoryNode;
        });
        setCategories(normalized);
      } else if (res.data.categories != null) {
        setCategories(DEFAULT_CATEGORY_SLUGS.map(s => ({ id: s, name: s, children: [], attributes: [] })));
      }
      if (typeof res.data.currencySymbol === 'string') {
        setCurrencySymbol(res.data.currencySymbol.trim() || defaultCurrency);
      } else if (res.data.currencySymbol != null) {
        setCurrencySymbol(defaultCurrency);
      }
      if (typeof res.data.heroVideoUrl === 'string') {
        setHeroVideoUrl(res.data.heroVideoUrl);
      } else {
        setHeroVideoUrl(null);
      }
      setHeroHomeText(normalizeHeroHomeTextConfig(res.data.heroHomeText));
      if (res.data.fromDatabase === false && showErrorToast) {
        toast.error(t('siteSettings.toastDbFallback'), { duration: 5000 });
      }
    } catch (err: unknown) {
      const ax = err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: { categories?: any[]; currencySymbol?: string } } }) : null;
      if (ax?.response?.data && Array.isArray(ax.response.data.categories)) {
        const normalized: CategoryNode[] = ax.response.data.categories.map(cat => {
          if (typeof cat === 'string') {
            return { id: cat, name: cat, children: [], attributes: [] };
          }
          return cat as CategoryNode;
        });
        setCategories(normalized);
      }
      if (ax?.response?.data && typeof ax.response.data.currencySymbol === 'string') {
        setCurrencySymbol(ax.response.data.currencySymbol.trim() || defaultCurrency);
      }
      setHeroHomeText(null);
      if (showErrorToast) {
        toast.error(t('siteSettings.toastFetchFailed'), { duration: 6000 });
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings(false);
  }, [fetchSettings]);

  const currencyLabel = useMemo(() => {
    const sym = currencySymbol.trim();
    const i18nKey = CURRENCY_I18N_KEY[sym];
    if (i18nKey) {
      const k = `currency.${i18nKey}`;
      const translated = t(k);
      if (translated !== k) return translated;
    }
    return CURRENCY_LABELS[sym] || sym;
  }, [currencySymbol, t, i18n.language]);

  const refreshWithToast = useCallback(() => fetchSettings(true), [fetchSettings]);
  const value: SiteSettingsContextType = {
    categories,
    currencySymbol,
    currencyLabel,
    heroVideoUrl,
    heroHomeText,
    loading,
    refresh: refreshWithToast
  };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}
